import { Inject, Injectable } from '@angular/core';
import { IDatabaseService } from 'src/app/shared/services/contracts/database/database-service';
import { DatabaseService } from 'src/app/shared/services/database.service';

interface SentryThrottledErrorState {
  _id?: string;
  count: number;
  lastSentAt?: string; // ISO string
}

/**
 * Throttles noisy Sentry errors.
 * Add patterns in `patterns`. Sends first and every Nth occurrence.
 */
@Injectable({ providedIn: 'root' })
export class SentryThrottleService {
  private readonly dbName = 'statePersister.db';

  constructor(@Inject(DatabaseService) private readonly dbService: IDatabaseService) {}

  /** Pattern registry. Add more entries to throttle additional errors. */
  private readonly patterns: Array<{
    id: string; // used to build extra payload key
    test: RegExp; // regex against error message
    key: string; // storage key suffix (sentry: prefix is added automatically)
    threshold: number; // send on first and every Nth occurrence
    tags?: Record<string, string>; // tags to add when sending
  }> = [
    {
      id: 'idbDisconnect',
      test: /indexed database server.*lost/i,
      key: 'idbDisconnect',
      threshold: 100,
      tags: { context: 'idb-issue' },
    },
    {
      // a data call answered with 401 (#27294): counted instead of ignored, so it stays measurable
      id: 'unauthenticatedCall',
      test: /^Not authenticated$/i,
      key: 'unauthenticatedCall',
      threshold: 25,
      tags: { context: 'unauthenticated-call' },
    },
    {
      // a native plugin method the installed binary does not implement (#27410): StatusBar < 7 on iOS,
      // missing EdgeToEdge on old Android. Repeats on every page, so count it instead of flooding
      id: 'pluginUnimplemented',
      test: /^not implemented$|plugin is not implemented on/i,
      key: 'pluginUnimplemented',
      threshold: 100,
      tags: { context: 'plugin-unimplemented' },
    },
    {
      id: 'missingControlName',
      test: /Cannot find control with unspecified name attribute/i,
      key: 'missingControlName',
      threshold: 250,
      tags: { context: 'unspecified-name' },
    },
  ];

  /** Sentry beforeSend: match pattern, throttle, enrich, return event or null. */
  public async beforeSend(event: any, hint?: { originalException?: any }): Promise<any | null> {
    try {
      const value = (hint?.originalException as Error)?.message || event?.exception?.values?.[0]?.value || '';
      const message = String(value);

      this.applyCapacitorFingerprint(event, hint?.originalException, message);

      const pattern = this.patterns.find(p => p.test.test(message));
      if (!pattern) {
        return event; // not a throttled pattern, handle as usual
      }

      const { shouldSend, state, threshold } = await this.incrementAndDecide(
        `sentry:${pattern.key}`,
        pattern.threshold
      );
      if (!shouldSend) return null;

      const lastOccurrenceAt = new Date().toISOString();

      event.extra = {
        ...(event.extra || {}),
        [pattern.id]: {
          count: state.count,
          threshold: threshold,
          lastSentAt: state.lastSentAt,
          lastOccurrenceAt: lastOccurrenceAt,
        },
      };
      event.tags = {
        ...(event.tags || {}),
        ...pattern.tags,
      };
      return event;
    } catch (e) {
      window.logger?.error?.('SentryThrottleService.beforeSend error', e);
      // In case of error in throttle, drop to avoid noise
      return null;
    }
  }

  /**
   * A rejected native plugin call reaches Sentry as a CapacitorException whose only stack frame is the
   * constructor: the error is built inside the injected native bridge, whose frames carry no file URL
   * and are dropped by the stack parser. On iOS this grouped every native failure (StatusBar, Camera,
   * Browser, Filesystem, ...) into one issue (#27410). Group them by plugin message instead, with
   * device specific ids and file paths stripped so the same failure stays one issue.
   *
   * `event.fingerprint` replaces Sentry's stack based grouping, see
   * https://docs.sentry.io/platforms/javascript/guides/capacitor/enriching-events/fingerprinting/#group-errors-more-aggressively
   */
  private applyCapacitorFingerprint(event: any, exception: any, message: string): void {
    if (!this.isCapacitorException(exception)) {
      return;
    }

    const normalized = this.normalizeNativeMessage(message || exception.errorMessage || '');
    if (!normalized) {
      return; // nothing to group by, keep Sentry's default grouping rather than one empty bucket
    }

    event.fingerprint = ['capacitor-plugin', normalized];
    event.tags = {
      ...(event.tags || {}),
      context: 'capacitor-plugin',
      ...(exception.code ? { capacitor_code: String(exception.code) } : {}),
    };
  }

  /**
   * Two copies of `@capacitor/core` are bundled (ionic-appauth ships its own), so `instanceof` is not
   * reliable. The class name survives the production build; `errorMessage` is the string the native
   * bridge copies onto the exception and only exists on native rejections.
   */
  private isCapacitorException(exception: any): boolean {
    if (!exception || typeof exception !== 'object') {
      return false;
    }
    const hasBridgeErrorMessage =
      Object.prototype.hasOwnProperty.call(exception, 'errorMessage') && typeof exception.errorMessage === 'string';
    return exception.constructor?.name === 'CapacitorException' || hasBridgeErrorMessage;
  }

  private normalizeNativeMessage(message: string): string {
    return (
      message
        // quoted paths first, they may contain spaces ('file:///.../Mein Befund.pdf')
        .replace(/(['"])(?:file:\/\/|\/data\/|\/var\/|\/private\/).*?\1/g, '$1<path>$1')
        .replace(/(?:file:\/\/|\/data\/|\/var\/|\/private\/)[^'"\s]+/g, '<path>')
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '<id>')
        .replace(/\s+/g, ' ')
        .trim()
    );
  }

  /** Increment counter for key and decide if we should send (1st or every Nth). */
  private async incrementAndDecide(
    key: string,
    threshold: number
  ): Promise<{ shouldSend: boolean; state: SentryThrottledErrorState; threshold: number }> {
    try {
      const storage = await this.dbService.getDatabase(this.dbName);
      const current: SentryThrottledErrorState = (await storage.getItem(key)) || { count: 0 };
      const next: SentryThrottledErrorState = { ...current, _id: key, count: (current.count || 0) + 1 };

      const isFirst = next.count === 1;
      const isThreshold = next.count % threshold === 0;
      const shouldSend = isFirst || isThreshold;

      if (shouldSend) {
        next.lastSentAt = new Date().toISOString();
      }

      await storage.setItem(key, next);
      return { shouldSend, state: next, threshold };
    } catch (error) {
      window.logger?.error?.(`SentryThrottleService error ${key}`, error);
      return { shouldSend: false, state: { count: 0 }, threshold };
    }
  }
}
